import React from 'react';
import { CircleMarker, Popup, Polyline } from 'react-leaflet';
import FullscreenMap from '../FullscreenMap';
import DrawingsLayer from '../map/DrawingsLayer';
import CareMarkers from '../map/CareMarkers';
import BuildingsLayer from '../map/BuildingsLayer';
import { severityColors } from '../dashboard/dashboardShared';

// Carte plein ecran de la situation communale (wrapper de FullscreenMap avec les couches metier)
const CommuneSituationMap = ({ open, onClose, mapCenter, zones, restrictions, careCenters, transports, evacPoints, incidents = [], drawings = [] }) => (
    <FullscreenMap
        open={open}
        onClose={onClose}
        title={`Carte de situation - Commune (${new Date().toLocaleString('fr-FR')})`}
        center={mapCenter}
        zoom={13}
        theme="dark"
        layers={[
            {
                id: 'incidents', label: 'Incidents (evenements)', color: '#a855f7',
                count: incidents.length,
                render: () => incidents.map(inc => (
                    <CircleMarker key={`finc-${inc.id}`} center={[inc.latitude, inc.longitude]} radius={14} pathOptions={{ color: severityColors[inc.severity] || '#a855f7', fillColor: severityColors[inc.severity] || '#a855f7', fillOpacity: 0.4 }}>
                        <Popup><div className="text-xs"><b>{inc.title}</b><br />{inc.description}<br /><span className="opacity-70">Gravite: {inc.severity}</span></div></Popup>
                    </CircleMarker>
                ))
            },
            {
                id: 'dessins', label: 'Dessins / marquages carte', color: '#f97316',
                count: drawings.length,
                render: () => <DrawingsLayer drawings={drawings} />
            },
            {
                id: 'zones_signalees', label: 'Zones dangereuses', color: '#ef4444',
                count: zones.filter(z => z.latitude && z.longitude && z.statut === 'signale').length,
                render: () => zones.filter(z => z.latitude && z.longitude && z.statut === 'signale').map(z => (
                    <CircleMarker key={`fz-${z.id}`} center={[z.latitude, z.longitude]} radius={16} pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.4 }}>
                        <Popup><div className="text-xs"><b>Zone dangereuse</b><br />{z.lieu}<br />{z.description}<br /><span className="opacity-70">{z.type_danger}</span></div></Popup>
                    </CircleMarker>
                ))
            },
            {
                id: 'zones_securisees', label: 'Zones securisees', color: '#22c55e',
                count: zones.filter(z => z.latitude && z.longitude && z.statut === 'securise').length,
                render: () => zones.filter(z => z.latitude && z.longitude && z.statut === 'securise').map(z => (
                    <CircleMarker key={`fzs-${z.id}`} center={[z.latitude, z.longitude]} radius={12} pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.3 }}>
                        <Popup><div className="text-xs"><b>Zone securisee</b><br />{z.lieu}</div></Popup>
                    </CircleMarker>
                ))
            },
            {
                id: 'restrictions', label: 'Restrictions routieres', color: '#f59e0b',
                count: restrictions.filter(r => r.latitude && r.longitude && r.statut === 'active').length,
                render: () => restrictions.filter(r => r.latitude && r.longitude && r.statut === 'active').map(r => (
                    <CircleMarker key={`fr-${r.id}`} center={[r.latitude, r.longitude]} radius={12} pathOptions={{ color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.4 }}>
                        <Popup><div className="text-xs"><b>Restriction</b><br />{r.voie_concernee || r.lieu}<br />{r.description}{r.deviation_proposee && <><br /><span className="opacity-70">Deviation: {r.deviation_proposee}</span></>}</div></Popup>
                    </CircleMarker>
                ))
            },
            {
                id: 'care', label: 'Centres CARE', color: '#3b82f6',
                count: careCenters.filter(c => c.latitude && c.longitude).length,
                render: () => <CareMarkers centers={careCenters} prefix="fc" />
            },
            {
                id: 'buildings', label: 'Bâtiments (état)', color: '#bd622c',
                render: () => <BuildingsLayer />
            },
            {
                id: 'transports', label: 'Transports collectifs (trajets)', color: '#06b6d4',
                count: transports.filter(t => t.statut === 'actif').length,
                render: () => transports.filter(t => t.statut === 'actif').flatMap(t => {
                    const items = [];
                    if (t.depart_lat && t.depart_lng) {
                        items.push(
                            <CircleMarker key={`ftd-${t.id}`} center={[t.depart_lat, t.depart_lng]} radius={8} pathOptions={{ color: '#06b6d4', fillColor: '#06b6d4', fillOpacity: 0.7 }}>
                                <Popup><div className="text-xs"><b>Depart: {t.ligne}</b><br />{t.point_depart}<br />{t.horaire_debut && `Debut: ${t.horaire_debut}`}{t.chauffeur && <><br />Chauffeur: {t.chauffeur}</>}</div></Popup>
                            </CircleMarker>
                        );
                    }
                    if (t.arrivee_lat && t.arrivee_lng) {
                        items.push(
                            <CircleMarker key={`fta-${t.id}`} center={[t.arrivee_lat, t.arrivee_lng]} radius={8} pathOptions={{ color: '#8b5cf6', fillColor: '#8b5cf6', fillOpacity: 0.7 }}>
                                <Popup><div className="text-xs"><b>Arrivee: {t.ligne}</b><br />{t.point_arrivee}{t.horaire_fin && <><br />Fin: {t.horaire_fin}</>}</div></Popup>
                            </CircleMarker>
                        );
                    }
                    if (t.depart_lat && t.depart_lng && t.arrivee_lat && t.arrivee_lng) {
                        items.push(
                            <Polyline key={`ftl-${t.id}`} positions={[[t.depart_lat, t.depart_lng], [t.arrivee_lat, t.arrivee_lng]]} pathOptions={{ color: '#06b6d4', weight: 3, opacity: 0.7, dashArray: '6 6' }} />
                        );
                    }
                    return items;
                })
            },
            {
                id: 'evacuation', label: 'Requisitions transport', color: '#d946ef',
                count: evacPoints.filter(e => e.latitude && e.longitude && e.statut !== 'terminee' && e.statut !== 'refusee').length,
                render: () => evacPoints.filter(e => e.latitude && e.longitude && e.statut !== 'terminee' && e.statut !== 'refusee').map(e => (
                    <CircleMarker key={`fe-${e.id}`} center={[e.latitude, e.longitude]} radius={12} pathOptions={{ color: '#d946ef', fillColor: '#d946ef', fillOpacity: 0.45 }}>
                        <Popup><div className="text-xs"><b>{e.organisme}</b><br />{e.nb_unites || 0} unite(s) - {(e.nb_unites || 0) * (e.capacite_unitaire || 0)} places<br />{e.lieu_prise_en_charge} → {e.destination}{e.contact_nom && <><br />Resp: {e.contact_nom}</>}{e.contact_telephone && <><br />{e.contact_telephone}</>}</div></Popup>
                    </CircleMarker>
                ))
            },
        ]}
        extraLegend={[
            ['bg-cyan-500', 'Depart transport'],
            ['bg-violet-500', 'Arrivee transport'],
        ]}
    />
);

export default CommuneSituationMap;
